'use strict';
/**
 * visitor-id.js — canonical first-party visitor id minter.
 *
 * Creates + persists a stable `visitorID` (cookie, 30 days, AND localStorage),
 * generated once per browser the SAME way the ROW pilot does: a v4-style UUID,
 * minted UNCONDITIONALLY when absent. Every downstream consumer reads this id:
 *   - cae-client.js       getViewerId() -> localStorage 'viewerId' || 'visitorID' || cookie …
 *   - signup-vid-link.js  visitorId()   -> SE_visitorIdentity.visitor_id || localStorage 'visitorID'
 *   - the scarcity engine + the Sleeknote free-patch injector (localStorage 'visitorID')
 *
 * MUST load BEFORE cae-client.js so the id exists before viewerId is first read
 * (otherwise cae-client mints its OWN 'viewerId' and the ids diverge, breaking the
 * CAE <-> users_drip join). Idempotent: an existing valid id (cookie or localStorage)
 * is reused. The legacy junk id '1' is treated as absent (matches ROW's getCookie).
 *
 * Ported from the ROW pilot so IL/EU create the visitor id identically to ROW.
 */
(function () {
  var VID_KEY = 'visitorID';

  function generateUUID() {
    var d = new Date().getTime();
    var d2 = (typeof performance !== 'undefined' && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      if (d > 0) { r = (d + r) % 16 | 0; d = Math.floor(d / 16); }
      else if (d2 > 0) { r = (d2 + r) % 16 | 0; d2 = Math.floor(d2 / 16); }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  function getCookie(name) {
    try {
      var nameEQ = name + '=';
      var ca = document.cookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i].trim();
        if (c.indexOf(nameEQ) === 0) { return c.substring(nameEQ.length); }
      }
    } catch (e) {}
    return null;
  }

  function setCookie(name, value, days) {
    if (!value || value === '1') return;   // never persist the legacy junk id (matches ROW)
    try {
      var expires = '';
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + value + expires + '; path=/';
    } catch (e) {}
  }

  try {
    var vid = getCookie(VID_KEY);
    if (vid === '1') vid = null;                       // legacy junk -> treat as absent
    if (!vid) {
      try { vid = (localStorage.getItem(VID_KEY) || '').trim(); } catch (e) { vid = ''; }
      if (vid === '1') vid = null;
    }
    if (!vid) { vid = generateUUID(); }                // mint UNCONDITIONALLY when none exists

    // Mirror to BOTH cookie (30d) + localStorage so every consumer reads the same id.
    setCookie(VID_KEY, vid, 30);
    try { localStorage.setItem(VID_KEY, vid); } catch (e) {}
    try { window.AGB_VisitorId = vid; } catch (e) {}   // sync accessor for anything that wants it

    // ── Country resolution (EU) ───────────────────────────────────────────
    // localStorage['country'] = what CAE (analytics `country` column) and
    // country-aware flows read. Priority (user decision 2026-07-05):
    //   1. SHOPIFY-NATIVE — window.Shopify.country (the buyer's Markets
    //      country) else Shopify's own IP-geo endpoint
    //      /browsing_context_suggestions.json. Zero backend of ours.
    //   2. FALLBACK ONLY — db-app-2 /api/whoami (GeoLite2 on request IP).
    // Both are request/IP based → work for EVERY visitor incl. first-time
    // anonymous (not just subscribers/returning). Shopify yields ISO codes
    // ("FR") while the cross-store vocabulary is full NAMES ("France" — ROW's
    // CAE column + TTB maps), so codes convert via the browser-built-in
    // Intl.DisplayNames; if that's unavailable we fall through to whoami
    // rather than mix vocabularies. Fail-open everywhere; heals the legacy
    // "null"/"undefined" poison strings.
    try {
      var cur = localStorage.getItem('country');
      if (!cur || cur === 'null' || cur === 'undefined') {
        var writeName = function (name) {
          try { if (name) localStorage.setItem('country', name); } catch (e) {}
        };
        var toName = function (codeOrName) {
          var s = String(codeOrName || '').trim();
          if (!s) return null;
          if (s.length !== 2) return s; // already a display name
          try {
            var n = new Intl.DisplayNames(['en'], { type: 'region' }).of(s.toUpperCase());
            return (n && n.toUpperCase() !== s.toUpperCase()) ? n : null;
          } catch (e) { return null; }
        };
        var whoamiFallback = function () {
          try {
            var wurl = 'https://whoami-432657747828.europe-west1.run.app/api/whoami?region_enabled=1&region_persist=0'
              + (vid ? '&visitorID=' + encodeURIComponent(vid) : '');
            fetch(wurl)
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (w) { writeName(w && w.country); })
              .catch(function () { /* fail-open: flows use their defaults */ });
          } catch (e) {}
        };
        var marketCode = '';
        try { marketCode = (window.Shopify && window.Shopify.country) ? String(window.Shopify.country).trim().toUpperCase() : ''; } catch (e) {}
        var marketName = toName(marketCode);
        // EU default-market override (Dorian 2026-07-14): 'SK' (Slovakia) is the
        // market Shopify drops a shopper into when it can't place them in a real
        // market of THIS store — e.g. an Israeli visitor (Israel is the IL store,
        // not a market here). It is almost never a genuine EU-store shopper's
        // country, so DON'T trust it: verify with geoip (whoami) and KEEP whatever
        // geoip returns. A real Slovak shopper still resolves to Slovakia via
        // geoip, so this only corrects the mis-placed-default case.
        if (marketCode === 'SK') {
          whoamiFallback();
        } else if (marketName) {
          writeName(marketName);
        } else {
          fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (d) {
              // Shopify has varied this response shape over time — same
              // defensive extraction as the TTB resolver.
              var code = d && (
                (d.detected_values && d.detected_values.country && (d.detected_values.country.handle || d.detected_values.country.value)) ||
                d.country_code ||
                (Array.isArray(d.suggestions) && (d.suggestions.filter(function (s) { return s && (s.part === 'country' || s.part === 'country_code'); })[0] || {}).value)
              ) || null;
              var name = toName(code);
              if (name) writeName(name); else whoamiFallback();
            })
            .catch(whoamiFallback);
        }
      }
    } catch (e) {}
  } catch (e) { /* never block the page on id minting */ }
})();
